import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilterPanel = ({ 
  filters, 
  activeFilters, 
  onFilterChange, 
  onClearFilters, 
  isOpen, 
  onToggle 
}) => {
  const filterCategories = [
    {
      key: 'industry',
      label: 'Industry',
      icon: 'Building2',
      options: filters?.industries
    },
    {
      key: 'serviceType',
      label: 'Service Type',
      icon: 'Briefcase',
      options: filters?.serviceTypes
    },
    {
      key: 'companySize',
      label: 'Company Size',
      icon: 'Users',
      options: filters?.companySizes
    },
    {
      key: 'location',
      label: 'Location',
      icon: 'MapPin',
      options: filters?.locations
    }
  ];

  const handleFilterToggle = (category, value) => {
    const currentFilters = activeFilters?.[category] || [];
    const newFilters = currentFilters?.includes(value)
      ? currentFilters?.filter(f => f !== value)
      : [...currentFilters, value];
    
    onFilterChange(category, newFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(activeFilters)?.reduce((count, filters) => count + filters?.length, 0);
  };

  return (
    <>
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-6">
        <Button
          variant="outline"
          onClick={onToggle}
          iconName="Filter"
          iconPosition="left"
          iconSize={16}
          fullWidth
        >
          Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
        </Button>
      </div>
      {/* Filter Panel */}
      <div className={`lg:block ${isOpen ? 'block' : 'hidden'} bg-card border border-border rounded-lg p-6 mb-6 lg:mb-0`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-text-primary flex items-center">
            <Icon name="Filter" size={20} className="mr-2" />
            Filters
          </h3>
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
              iconSize={14}
            >
              Clear All
            </Button>
          )}
        </div>

        <div className="space-y-6">
          {filterCategories?.map((category) => (
            <div key={category?.key}>
              <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center">
                <Icon name={category?.icon} size={16} className="mr-2" />
                {category?.label}
              </h4>
              <div className="space-y-2">
                {category?.options?.map((option) => {
                  const isActive = activeFilters?.[category?.key]?.includes(option) || false;
                  return (
                    <label
                      key={option}
                      className="flex items-center space-x-3 cursor-pointer hover:bg-muted p-2 rounded-md transition-colors duration-200"
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={() => handleFilterToggle(category?.key, option)}
                        className="w-4 h-4 text-accent border-border rounded focus:ring-accent focus:ring-2"
                      />
                      <span className={`text-sm ${isActive ? 'text-text-primary font-medium' : 'text-text-secondary'}`}>
                        {option}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Active Filters Summary */}
        {getActiveFilterCount() > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <h4 className="text-sm font-medium text-text-primary mb-3">Active Filters</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(activeFilters)?.map(([category, filters]) =>
                filters?.map((filter) => (
                  <span
                    key={`${category}-${filter}`}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent"
                  >
                    {filter}
                    <button
                      onClick={() => handleFilterToggle(category, filter)}
                      className="ml-2 hover:text-accent/80"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FilterPanel;